import { useState } from "react";
import { Sheet } from "../../components/Sheet";
import { Button } from "../../components/primitives";
import type { Advisory } from "../../lib/qa";

/**
 * OVERRIDE ATTESTATION — dismissing an advisory is one gesture, but it is never silent.
 *
 * The finding was raised against text the operator already approved, so keeping it is a legitimate
 * choice — but it is recorded, with a reason, into the run's provenance. A real modal is correct
 * here (the operator is committing a record), so this reuses the Sheet. Bulk dismissal cannot skip
 * it, and a protected-fact violation never reaches this component — that one cannot be overridden.
 */
const REASONS = [
  "Style preference",
  "Intentional emphasis",
  "Funder-specific phrasing",
  "Reviewed — acceptable"
];

export function OverrideAttestation({
  advisory,
  onCancel,
  onConfirm
}: {
  advisory: Advisory;
  onCancel: () => void;
  onConfirm: (reason: string, note: string) => void;
}) {
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState("");
  return (
    <Sheet
      title="Keep this as it is?"
      testId="override-attestation"
      onClose={onCancel}
      footer={
        <Button variant="primary" block data-testid="override-confirm" onClick={() => onConfirm(reason, note)}>
          Record &amp; dismiss
        </Button>
      }
    >
      <div className="attest-body">
        <p className="attest-finding">{advisory.message}</p>
        <fieldset className="attest-reasons">
          <legend>Why are you keeping it?</legend>
          {REASONS.map((option) => (
            <label key={option} className="attest-chip" data-active={reason === option ? "true" : "false"}>
              <input
                type="radio"
                name="override-reason"
                value={option}
                checked={reason === option}
                onChange={() => setReason(option)}
              />
              {option}
            </label>
          ))}
        </fieldset>
        <label className="attest-note">
          <span>Note (optional)</span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            placeholder="Anything a later reviewer should know."
          />
        </label>
        <p className="attest-foot">Recorded in this run's provenance. It does not leave the app.</p>
      </div>
    </Sheet>
  );
}
